import Lottie from 'lottie-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { logError } from '../../../lib/logging'
import Button from '../Button'
import { ContactNameIcon } from '../contacts/ContactNameIcon'
import { SmartForm } from '../form/SmartForm'
import Companies from '../Icon/CompaniesIcon'
import Spinner from '../Spinner'
import * as error from './call_error.json'
import { callInitiationValidator } from './callInitiationValidator'
import { useOutboundCall } from './OutboundCallProvider'
import { PostCallModal, PostCallModalHeader } from './PostCallModal'
import FormHiddenInput from 'app/components/form/FormHiddenInput'
import FormPhone from 'app/components/form/FormPhone'
import FormSelect from 'app/components/form/FormComboSelect'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useOutboundNumbers } from './useOutboundNumbers'
import type { ContactJson } from 'app/types/entities'
import { closeModal, maximizeModal, showModal } from 'app/lib/modal'
import { gqlMutation } from 'app/lib/graphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { graphql } from 'app/generated/gql'

const INITIATE_CALL_MUTATION = graphql(`
  mutation InitiateCall($input: InitiateCallInput!) {
    initiateCall(input: $input) {
      success
      callId
      calledNumber
      error
    }
  }
`)

type PhoneOption = {
  label: string
  value: string
}

function getPhoneOptions(contact: ContactJson): PhoneOption[] {
  const phoneFields = [
    { label: `Cell Phone`, value: contact.cellPhone },
    { label: `Home Phone`, value: contact.homePhone },
    { label: `Work Phone`, value: contact.workPhone },
  ]

  return phoneFields
    .filter((field) => field.value && field.value.replace(/\D/g, ``).length === 10)
    .map((field) => ({
      label: `${field.label}: ${formatPhoneNumber(field.value!)}`,
      value: field.value!,
    }))
}

type CallInitiationModalProps = {
  contact: ContactJson
  onCallStarted?: () => void
  onContactRefresh?: () => void
}

export function CallInitiationModal({ contact, onCallStarted, onContactRefresh }: CallInitiationModalProps) {
  const me = useUser()
  const [loading, setLoading] = useState(false)
  const { startTrackingCall, activeCall, clearActiveCall } = useOutboundCall()
  const phoneOptions = useMemo(() => getPhoneOptions(contact), [contact])
  const [showResult, setShowResult] = useState<`success` | `failure` | null>(null)
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastSubmittedPayloadRef = useRef<Record<string, string> | null>(null)
  const matchingCall = activeCall?.contactId === contact.id
  const currentStatus = activeCall?.status
  const [location, locationFetching] = useOutboundNumbers(contact.locationId)

  const defaultValues = useMemo(
    () => ({
      contactId: contact.id,
      fromNumber: formatPhoneNumber(
        (typeof window !== `undefined` ? localStorage.getItem(`outboundCallFromNumber-${me?.id}`) : null) ||
          location?.officeMainNumber ||
          ``,
      ),
      toNumber: phoneOptions[0]?.value || ``,
    }),
    [contact.id, location?.officeMainNumber, phoneOptions],
  )

  const onSubmit = (values: any) => {
    setLoading(true)
    setShowResult(null)

    // Save fromNumber to localStorage for future calls
    if (typeof window !== `undefined` && values.fromNumber) {
      localStorage.setItem(`outboundCallFromNumber-${me?.id}`, values.fromNumber)
    }

    lastSubmittedPayloadRef.current = values
    bg(async () => {
      await submitCallRequest(values)
    })
  }

  const onCallInitiated = (data) => {
    setLoading(false)

    if (data?.success && data?.callId) {
      notifySuccess(`Call initiated successfully`)
      startTrackingCall({
        callId: data.callId,
        contactId: contact.id,
        contactName: contact.name,
        calledNumber: data.calledNumber,
      })
      if (onCallStarted && contact.id) {
        onCallStarted()
      }
      // Immediately open PostCallModal instead of showing progress animation
      showModal(
        <PostCallModal
          callId={data.callId}
          calledNumber={data.calledNumber}
          contact={contact}
          onSubmit={handleClose}
          onContactRefresh={onContactRefresh}
        />,
        {
          title: <PostCallModalHeader />,
          isFullScreen: false,
          removePadding: true,
          closable: false,
          showMinimizeButton: true,
          useRadix: true,
        },
        false,
      )
    } else {
      notifyError(data?.error || `Failed to initiate call`)
    }
  }

  const handleClose = () => {
    clearActiveCall()
    closeModal()
  }

  const submitCallRequest = async (payload: Record<string, string>) => {
    try {
      const input = {
        contactId: payload.contactId,
        fromNumber: payload.fromNumber,
        toNumber: payload.toNumber,
        primaryTrackingNumber: payload.primaryTrackingNumber,
      }
      const { data, error: gqlErr } = await gqlMutation(INITIATE_CALL_MUTATION, { input })
      if (gqlErr) throw new Error(gqlErr.message)
      onCallInitiated(data?.initiateCall)
    } catch (err) {
      logError(`Failed to initiate call:`, err)
      setLoading(false)
      notifyError(`Failed to retry the call. Please try again.`)
    }
  }

  const handleRetry = () => {
    const payload = lastSubmittedPayloadRef.current
    if (!payload) {
      notifyError(`Unable to retry call. Please open the form and submit again.`)
      return
    }

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current)
      successTimeoutRef.current = null
    }

    clearActiveCall()
    setShowResult(null)
    setLoading(true)

    bg(async () => {
      await submitCallRequest(payload)
    })
  }

  // Handle call status changes - since we open PostCallModal immediately,
  // we only need to handle retry flow for failed calls
  useEffect(() => {
    if (!matchingCall) {
      return
    }

    if (currentStatus === `failed`) {
      maximizeModal()
      setShowResult(`failure`)
    } else if (currentStatus === `polling`) {
      setShowResult(null)
    }
  }, [matchingCall, currentStatus])

  useEffect(() => {
    if (!activeCall) {
      setShowResult(null)
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
        successTimeoutRef.current = null
      }
    }
  }, [activeCall])

  const contactInfoHeader = (
    <div className="flex items-center gap-2 bg-surface-subtle px-6 py-3">
      <ContactNameIcon contact={contact} heightClassName="h-10" widthClassName="w-10" />
      <div className="flex h-10 flex-col justify-between">
        <p className="text-strong text-ui-sm">{contact.name}</p>
        <p className="text-subtle text-body-xs">{formatPhoneNumber(activeCall?.calledNumber)}</p>
      </div>
    </div>
  )

  const isFailure = showResult === `failure` || (matchingCall && currentStatus === `failed`)

  if (phoneOptions.length === 0) {
    return (
      <div className="p-4">
        <p className="text-body-sm text-default">
          No valid phone numbers available for this contact. Please add a US/Canada phone number to enable calling.
        </p>
        <div className="mt-4 flex justify-end">
          <Button label="Close" mode="white" onClick={handleClose} />
        </div>
      </div>
    )
  }

  if (isFailure) {
    return (
      <>
        {contactInfoHeader}
        <div className="flex h-[246px] w-full flex-col items-center justify-center gap-y-8 md:w-[512px]">
          <Lottie animationData={error} style={{ width: `72px`, height: `72px` }} loop={false} />
          <p className="text-strong text-ui-sm">Call failed</p>
        </div>
        <div className="sticky bottom-0 flex w-full items-center justify-end gap-2 rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
          <Button label="Cancel" mode="white" onClick={handleClose} disabled={loading} />
          <Button label="Try Again" mode="primary" onClick={handleRetry} loading={loading} />
        </div>
      </>
    )
  }

  if (!location && locationFetching) {
    return (
      <div className="flex h-[246px] w-full items-center justify-center md:w-[512px]">
        <Spinner size={8} />
      </div>
    )
  }

  return (
    <>
      <SmartForm
        id="call-initiation-form"
        validator={callInitiationValidator}
        defaultValues={defaultValues}
        className="!mt-0 w-full grow-0 justify-start !gap-y-4 px-6 py-4 md:w-[512px]"
        hideButtons
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSubmit}
        onValidationError={(errors) => {
          const msg = Object.values(errors).filter(Boolean).join(`, `)
          if (msg) notifyError(msg)
        }}
      >
        <FormHiddenInput name="contactId" value={contact.id} />
        <FormHiddenInput name="primaryTrackingNumber" value={location?.primaryTrackingNumber} />

        <p className="col-span-6 mb-4 text-body-xs text-subtle">
          First, we'll call your phone. Once you answer, we'll call the contact’s phone. From their perspective, it's
          like you called directly from your tracking number.
        </p>
        <div className="relative col-span-6 mb-4 flex flex-col gap-y-4 pl-8">
          <FormPhone
            name="fromNumber"
            label="Your number"
            placeholder="(555) 555-5555"
            labelClassName="mb-2 text-body-sm text-subtle"
          />
          <FormSelect
            name="toNumber"
            label="Contact's number"
            options={phoneOptions}
            labelClassName="mb-2 text-body-sm text-subtle"
            required
          />
          <div className="absolute left-0 top-0 flex flex-col items-center">
            <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-600">
              <Companies className="h-3 w-3 text-on-color" />
            </div>
            <div className="my-1 h-[85px] w-[1px] bg-surface-strong" />
            <ContactNameIcon contact={contact} heightClassName="h-5" widthClassName="w-5" isTiny />
          </div>
        </div>
      </SmartForm>
      <div className="sticky bottom-0 flex w-full items-center justify-end gap-2 rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <Button label="Cancel" mode="white" onClick={handleClose} disabled={loading} />
        <Button label="Start Call" mode="primary" type="submit" form="call-initiation-form" loading={loading} />
      </div>
    </>
  )
}
